/**
 * The filters the Breakdown and Runs tabs share, and the toolbar that sets
 * them. One state on the page feeds both tabs, so switching between them
 * keeps describing the same runs. The order follows the Runs columns: When
 * (held inside the period, a month or a year), Process, Model, Who, Status, then a cost range
 * and a time range. A tab may put its own control first (Breakdown's "By").
 */

import { useMemo, type ReactNode } from 'react';
import { DollarSign, Timer } from 'lucide-react';

import type { Period, RunListParams, Usage, UsageBucket } from '@/api/types';
import { DateFilter, dateBounds, type DateFilterValue, type DatePreset } from '@/ui/DateFilter';
import { formatUsd } from '@/ui/format';
import { MultiSelect } from '@/ui/MultiSelect';
import { ANY_RANGE, RangeFilter, type NumberRange } from '@/ui/RangeFilter';

import { STATUSES, STATUS_STYLE, formatSeconds, isCurrentPeriod, isYear, periodDays } from './usageUi';

export interface RunFilters {
  when: DateFilterValue;
  /** The When days run in UTC: after opening a day from a group or the chart (whose days are UTC), or when Runs reads times in UTC. */
  whenUtc: boolean;
  processes: string[];
  models: string[];
  /** User ids, or 'system'. */
  users: string[];
  statuses: string[];
  /** USD. */
  cost: NumberRange;
  /** Seconds. */
  time: NumberRange;
}

export const WHOLE_MONTH: DateFilterValue = { kind: 'preset', preset: 'all' };

export const NO_FILTERS: RunFilters = {
  when: WHOLE_MONTH,
  whenUtc: false,
  processes: [],
  models: [],
  users: [],
  statuses: [],
  cost: ANY_RANGE,
  time: ANY_RANGE,
};

/** Quick ranges for the current month or year (the server clips them to the period anyway). */
const CURRENT_MONTH_PRESETS: DatePreset[] = ['all', 'today', 'yesterday', '7d'];
const CURRENT_YEAR_PRESETS: DatePreset[] = ['all', 'today', 'yesterday', '7d', '30d', '90d'];

function presetsFor(period: Period): DatePreset[] {
  if (!isCurrentPeriod(period)) return ['all'];
  return isYear(period) ? CURRENT_YEAR_PRESETS : CURRENT_MONTH_PRESETS;
}

export function isFiltered(f: RunFilters): boolean {
  return Boolean(
    f.when.kind !== 'preset' ||
      f.when.preset !== 'all' ||
      f.processes.length ||
      f.models.length ||
      f.users.length ||
      f.statuses.length ||
      f.cost.min !== null ||
      f.cost.max !== null ||
      f.time.min !== null ||
      f.time.max !== null,
  );
}

type Matching = Omit<RunListParams, 'sort' | 'order' | 'page' | 'page_size'>;

/** The filters as API parameters, for the run list and its grouped view alike. */
export function useMatchingParams(period: Period, f: RunFilters): Matching {
  const bounds = useMemo(() => dateBounds(f.when, f.whenUtc), [f.when, f.whenUtc]);
  return {
    period,
    processes: f.processes,
    models: f.models,
    users: f.users,
    statuses: f.statuses,
    created_from: bounds.from,
    created_to: bounds.to,
    cost_min: f.cost.min,
    cost_max: f.cost.max,
    latency_min_ms: f.time.min === null ? null : Math.round(f.time.min * 1000),
    latency_max_ms: f.time.max === null ? null : Math.round(f.time.max * 1000),
  };
}

export function RunFilterBar({
  period,
  data,
  filters,
  onChange,
  lead,
  trail,
  utc = false,
}: {
  period: Period;
  /** The period's usage: its breakdowns are the filters' options and counts. */
  data: Usage;
  filters: RunFilters;
  onChange: (patch: Partial<RunFilters>) => void;
  /** A tab's own control, before the filters. */
  lead?: ReactNode;
  /** A tab's own control, at the far end of the toolbar (Runs' Local / UTC). */
  trail?: ReactNode;
  /** Days picked in the When filter read in UTC rather than local time. */
  utc?: boolean;
}) {
  const days = periodDays(period);

  return (
    <div className="toolbar filter-panel">
      {lead && (
        <>
          {lead}
          <span className="filter-divider" aria-hidden="true" />
        </>
      )}
      <DateFilter
        label="When"
        value={filters.when}
        utc={filters.whenUtc}
        presets={presetsFor(period)}
        allLabel={isYear(period) ? 'Whole year' : 'Whole month'}
        min={days.first}
        max={days.last}
        onChange={(value) => onChange({ when: value, whenUtc: utc })}
      />
      <GroupFilters data={data} filters={filters} onChange={onChange} />
      <RangeFilter
        label="Cost"
        allLabel="Any cost"
        Icon={DollarSign}
        prefix="$"
        step="0.0001"
        value={filters.cost}
        format={formatUsd}
        onChange={(value) => onChange({ cost: value })}
      />
      <RangeFilter
        label="Duration"
        allLabel="Any duration"
        Icon={Timer}
        unit="s"
        step="0.1"
        value={filters.time}
        format={formatSeconds}
        onChange={(value) => onChange({ time: value })}
      />
      {isFiltered(filters) && (
        <button type="button" className="btn btn-ghost" onClick={() => onChange(NO_FILTERS)}>
          Clear filters
        </button>
      )}
      {trail && (
        <>
          <div className="spacer" />
          {trail}
        </>
      )}
    </div>
  );
}

function bucketOptions(buckets: UsageBucket[]) {
  return buckets.filter((b) => b.key !== '—').map((b) => ({ value: b.key, label: b.label, meta: b.runs }));
}

/** Process, Model, Who and Status: the period's breakdowns, with their run counts. */
function GroupFilters({
  data,
  filters,
  onChange,
}: {
  data: Usage;
  filters: RunFilters;
  onChange: (patch: Partial<RunFilters>) => void;
}) {
  const statusCount: Record<string, number> = {
    succeeded: data.totals.succeeded,
    failed: data.totals.failed,
    refused: data.totals.refused,
  };
  return (
    <>
      <MultiSelect
        label="Filter by process"
        allLabel="All processes"
        noun="processes"
        options={bucketOptions(data.by_process)}
        value={filters.processes}
        onChange={(value) => onChange({ processes: value })}
      />
      <MultiSelect
        label="Filter by model"
        allLabel="All models"
        noun="models"
        options={bucketOptions(data.by_model)}
        value={filters.models}
        onChange={(value) => onChange({ models: value })}
      />
      <MultiSelect
        label="Filter by who"
        allLabel="Everyone"
        noun="people"
        searchable
        options={bucketOptions(data.by_user)}
        value={filters.users}
        onChange={(value) => onChange({ users: value })}
      />
      <MultiSelect
        label="Filter by status"
        allLabel="All statuses"
        noun="statuses"
        options={STATUSES.map((s) => ({ value: s, label: STATUS_STYLE[s].label, meta: statusCount[s] }))}
        value={filters.statuses}
        onChange={(value) => onChange({ statuses: value })}
      />
    </>
  );
}
